import { useCallback, useEffect, useRef, useState } from 'react';

import { PAGINATION_LIMIT } from '@constants/generic';
import { PaymentCollectionType } from '@features/group-management/constants';
import {
  AddFromMembersListItem,
  GetFeeCategoryDataFromConfig
} from '@features/group-management/types';
import { useLazyGetMembersQuery } from '@features/member-management/queries/MemberQuery';
import { MemberType } from '@features/member-management/types';
import { FeeConfigurationType } from '@types';
import { roundTo2 } from '@utils/generic';
import { buildQueryString } from '@utils/queryStringBuilder';

interface UseMemberDetailsProps {
  branchId: string;
  organizationId: string;
  groupAmount: string;
  groupDueDate: string;
  groupName?: string;
  feeCategoryData?: FeeConfigurationType[];
  searchTerm: string;
  page: number;
  isActiveGroup: boolean;
  setPage: (page: number) => void;
  groupPaymentCollectionType?: PaymentCollectionType;
  groupFrequency?: number;
  customFrequencyRequired?: boolean;
  sessionsPerCycle?: number;
}

const useMemberDetails = ({
  branchId,
  organizationId,
  groupAmount,
  groupDueDate,
  groupName,
  feeCategoryData,
  searchTerm,
  page,
  isActiveGroup,
  setPage,
  groupPaymentCollectionType,
  groupFrequency,
  customFrequencyRequired,
  sessionsPerCycle
}: UseMemberDetailsProps) => {
  const [memberMap, setMemberMap] = useState<
    Map<string, AddFromMembersListItem>
  >(new Map());
  const [currentPageIds, setCurrentPageIds] = useState<string[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const groupAmountInitial = useRef(groupAmount);

  const [
    getAllMembersQuery,
    { data: membersData, isLoading, isSuccess, isFetching }
  ] = useLazyGetMembersQuery();

  const getFeeCategoryDataFromConfig: GetFeeCategoryDataFromConfig =
    useCallback(
      (config) =>
        config?.map((feeConfig) => ({
          id: feeConfig.branch_fee_category_id,
          label: feeConfig.category,
          account_detail_id: feeConfig.account_detail_id,
          value: feeConfig.amount,
          isManuallyUpdated: feeConfig.isManuallyUpdated
        })) || [],
      []
    );

  const updateMember = (
    memberId: string,
    updates: Partial<AddFromMembersListItem>
  ) => {
    // useCallback(
    // (memberId: string, updates: Partial<AddFromMembersListItem>) => {
    setMemberMap((prev) => {
      const newMap = new Map(prev);
      const existingMember = newMap.get(memberId);
      if (existingMember) {
        newMap.set(memberId, { ...existingMember, ...updates });
      }
      return newMap;
    });
  };
  //   },
  //   []
  // );

  const getAllMembers = useCallback(() => {
    const queryObject = {
      ...(searchTerm?.trim() && { search: searchTerm?.trim() }),
      limit: PAGINATION_LIMIT,
      page: page - 1
    };
    const queryString = buildQueryString(queryObject);
    getAllMembersQuery({
      branchId,
      organizationId,
      queryString
    });
  }, [getAllMembersQuery, branchId, organizationId, searchTerm, page]);

  const refetchMembers = useCallback(() => {
    if (page > 1) setPage(1);
    else getAllMembers();
  }, [getAllMembers, page, setPage]);

  const getDueAmount = (
    member: AddFromMembersListItem | undefined,
    totalGroupAmount: number
  ) => {
    if (member?.isAmountManuallyEdited) {
      return member.dueAmount;
    }
    return groupAmount || totalGroupAmount.toString();
  };

  useEffect(() => {
    if (isSuccess && membersData && !isFetching) {
      const totalGroupAmount = roundTo2(
        feeCategoryData?.reduce(
          (sum, category) => sum + (Number(category.amount) || 0),
          0
        ) ?? 0
      );

      const memberFeeCategoryData =
        getFeeCategoryDataFromConfig(feeCategoryData);

      setMemberMap((prev) => {
        const newMap = new Map(prev);
        membersData?.data?.forEach((member: MemberType) => {
          const existingMember = newMap.get(member.id);

          newMap.set(member.id, {
            id: member.id,
            name: member.name,
            mobile: member.mobile,
            countryCode: member.country_code,
            dueAmount: getDueAmount(existingMember, totalGroupAmount),
            dueDate: existingMember?.dueDate || groupDueDate,
            admissionNumber: member?.admission_number,
            isAlreadyPresent: member.groups?.some(
              (group) => group.name === groupName
            ),
            isAmountManuallyEdited: !!existingMember?.isAmountManuallyEdited,
            feeCategoryData:
              existingMember?.feeCategoryData || memberFeeCategoryData,
            paymentCollectionType:
              existingMember?.paymentCollectionType ||
              groupPaymentCollectionType,
            interval: existingMember?.interval || groupFrequency?.toString(),
            isManualChangeMadeForDueDate:
              !!existingMember?.isManualChangeMadeForDueDate,
            isManualChangeMadeForDueAmount:
              !!existingMember?.isManualChangeMadeForDueAmount,
            isManualChangeMadeForStartDate:
              !!existingMember?.isManualChangeMadeForStartDate,
            isManualChangeMadeForSessionsPerCycle:
              !!existingMember?.isManualChangeMadeForSessionsPerCycle,
            sessionsPerCycle:
              existingMember?.isManualChangeMadeForSessionsPerCycle
                ? existingMember.sessionsPerCycle
                : sessionsPerCycle || 0,
            startDate: existingMember?.startDate || '',
            isDeletionInProgress: !!member.is_deletion_in_progress
          });
        });

        return newMap;
      });

      const newIds = membersData?.data?.map((member: MemberType) => member.id);

      setCurrentPageIds(newIds || []);
      setTotalCount(membersData.pagination.total_rows);
      groupAmountInitial.current = groupAmount;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSuccess,
    membersData,
    groupAmount,
    groupDueDate,
    groupName,
    feeCategoryData,
    isActiveGroup,
    getFeeCategoryDataFromConfig,
    customFrequencyRequired,
    groupPaymentCollectionType,
    groupFrequency,
    isFetching
  ]);

  useEffect(() => {
    refetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, searchTerm]);

  return {
    memberMap,
    membersData,
    currentPageIds,
    totalCount,
    isLoading,
    isSuccess,
    updateMember,
    getAllMembers,
    getFeeCategoryDataFromConfig
  };
};

export default useMemberDetails;
